import { useEffect, useState } from "react";
import { ArrowLeft, BarChart3, Calendar, CheckCircle2, Clock, Download, FileBadge, Loader2, MapPin, Pencil, RefreshCw, Trophy, Users } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import api, { downloadFile, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function RisultatoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [comp, setComp] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [editing, setEditing] = useState(null); // {athlete, judge, scores}
  const [editScores, setEditScores] = useState({ tecnica: "", coreografia: "", musicalita: "" });
  const [saving, setSaving] = useState(false);

  const openEdit = (athlete, judge, dance) => {
    setEditing({ athlete, judge, dance });
    if (dance) {
      const ds = (judge.dances?.[dance]?.scores) || {};
      setEditScores({
        tecnica: ds.tecnica ?? "",
        coreografia: ds.coreografia ?? "",
        musicalita: ds.musicalita ?? "",
      });
    } else {
      setEditScores({
        tecnica: judge.scores.tecnica ?? "",
        coreografia: judge.scores.coreografia ?? "",
        musicalita: judge.scores.musicalita ?? "",
      });
    }
  };

  const handleSaveScores = async () => {
    const payload = {};
    for (const k of ["tecnica", "coreografia", "musicalita"]) {
      const v = editScores[k];
      if (v === "" || v === null || v === undefined) continue;
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 10) {
        toast.error("I voti devono essere numeri interi da 1 a 10");
        return;
      }
      payload[k] = n;
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Inserisci almeno un voto");
      return;
    }
    if (editing.dance) payload.dance = editing.dance;
    setSaving(true);
    try {
      await api.put(
        `/competitions/${id}/admin/scores/${editing.athlete.id}/${editing.judge.judge_id}`,
        payload,
      );
      toast.success("Voti aggiornati");
      setEditing(null);
      await load(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadAthlete = async (athleteId, athleteName) => {
    setDownloadingId(athleteId);
    try {
      await downloadFile(`/competitions/${id}/certificates/${athleteId}`, `attestato_${athleteName}.pdf`);
      toast.success("Attestato scaricato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingZip(true);
    try {
      await downloadFile(`/competitions/${id}/certificates.zip`, "attestati.zip");
      toast.success("Download avviato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloadingZip(false);
    }
  };

  const load = async (manual = false) => {
    if (manual) setRefreshing(true); else setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get(`/competitions/${id}`),
        api.get(`/competitions/${id}/judging-summary`),
      ]);
      setComp(c.data);
      setSummary(s.data);
      if (manual) toast.success("Risultati aggiornati");
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/admin/risultati", { replace: true });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !comp || !summary) {
    return (
      <Layout>
        <div className="p-10 text-center text-slate-500 text-sm">Caricamento…</div>
      </Layout>
    );
  }

  const isLatin = summary.type === "latin";
  const danceLabels = summary.dance_labels || {};
  const dances = summary.dances || [];
  const fmtAvg = (v) => v == null ? <span className="text-slate-300">—</span> : (Number.isInteger(v) ? v : v.toFixed(2).replace(/\.?0+$/, ""));

  return (
    <Layout>
      <Link to="/admin/risultati" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4 sm:mb-6" data-testid="back-to-results">
        <ArrowLeft className="h-4 w-4" /> Torna ai risultati
      </Link>

      <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 sm:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
              <Trophy className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Risultati Competizione · {isLatin ? "Latin Style" : "Ballo Singolo"}</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-1.5 break-words">{comp.name}</h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-600 mt-2">
                <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{comp.location}</span>
                <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{new Date(comp.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</span>
                <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{summary.judges_count} giudic{summary.judges_count === 1 ? "e" : "i"}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {!isLatin && (
              <Button
                onClick={handleDownloadAll}
                disabled={downloadingZip || summary.athletes.length === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="download-all-zip"
              >
                {downloadingZip ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Genera Tutti gli Attestati
              </Button>
            )}
            <Button onClick={() => load(true)} disabled={refreshing} variant="outline" data-testid="refresh-detail">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
          </div>
        </div>
        <p className="text-sm text-slate-500 mt-4">
          {summary.finalized_count} di {summary.judges_count} giudic{summary.judges_count === 1 ? "e ha" : "i hanno"} concluso la votazione.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
          <BarChart3 className="h-5 w-5" />
        </div>
        <h2 className="font-display text-xl font-semibold text-slate-900">Dettaglio voti per atleta</h2>
      </div>

      {summary.athletes.length === 0 ? (
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-10 text-center text-slate-500 text-sm">Nessun atleta in questa competizione.</div>
      ) : (
        <div className="space-y-5">
          {summary.athletes.map((a) => {
            if (isLatin) {
              return (
                <div key={a.id} className="rounded-2xl ring-1 ring-slate-200 overflow-hidden bg-white" data-testid={`result-athlete-${a.id}`}>
                  <div className="bg-slate-50 px-5 py-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-slate-200">
                    <span className="font-mono font-bold text-blue-700 text-2xl">{a.number}</span>
                    <span className="font-display font-semibold text-slate-900 text-lg flex-1 min-w-0">{a.name}</span>
                    <Badge variant="outline" className="border-slate-300 text-slate-700">{a.type}</Badge>
                    {a.classe && <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Cl. {a.classe} · {a.fascia_eta}</Badge>}
                    {a.grand_total != null ? (
                      <span className="font-display font-bold text-xl tabular-nums text-white bg-blue-700 px-4 py-1.5 rounded-md" data-testid={`grand-total-${a.id}`}>
                        TOT {a.grand_total}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic text-sm">Totale in attesa</span>
                    )}
                  </div>

                  <div className="p-4 sm:p-5 space-y-5">
                    {dances.map((d) => {
                      const avgs = a.dance_averages?.[d] || {};
                      return (
                        <div key={d} className="rounded-xl ring-1 ring-fuchsia-100 overflow-hidden">
                          <div className="bg-fuchsia-50 px-4 py-2 border-b border-fuchsia-100 flex items-center justify-between">
                            <h3 className="font-display font-bold text-fuchsia-800 uppercase tracking-wider text-sm">{danceLabels[d] || d}</h3>
                            {avgs.totale != null && (
                              <span className="text-xs font-bold text-fuchsia-900">Totale ballo: <span className="font-display tabular-nums text-base">{fmtAvg(avgs.totale)}</span></span>
                            )}
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-left min-w-[600px]">
                              <thead className="bg-white text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                                <tr>
                                  <th className="px-5 py-2.5 font-semibold">Giudice</th>
                                  <th className="px-5 py-2.5 font-semibold w-24 text-center">Tec.</th>
                                  <th className="px-5 py-2.5 font-semibold w-24 text-center">Cor.</th>
                                  <th className="px-5 py-2.5 font-semibold w-24 text-center">Mus.</th>
                                  <th className="px-5 py-2.5 font-semibold w-24 text-center">Totale</th>
                                </tr>
                              </thead>
                              <tbody>
                                {a.judges.map((j) => {
                                  const ds = j.dances?.[d]?.scores || {};
                                  return (
                                    <tr key={j.judge_id} className="border-t border-slate-100">
                                      <td className="px-5 py-2.5">
                                        <div className="font-medium text-slate-900 inline-flex items-center gap-2 flex-wrap">
                                          <span>{j.judge_name}</span>
                                          {j.finalized ? (
                                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1 text-xs"><CheckCircle2 className="h-3 w-3" /> Concluso</Badge>
                                          ) : (
                                            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 gap-1 text-xs"><Clock className="h-3 w-3" /> In attesa</Badge>
                                          )}
                                          <button
                                            type="button"
                                            onClick={() => openEdit(a, j, d)}
                                            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline ml-1"
                                            data-testid={`edit-scores-${a.id}-${d}-${j.judge_id}`}
                                          >
                                            <Pencil className="h-3 w-3" /> Modifica
                                          </button>
                                        </div>
                                      </td>
                                      {["tecnica","coreografia","musicalita"].map((k) => (
                                        <td key={k} className="px-5 py-2.5 text-center">
                                          {ds[k] != null ? (
                                            <span className="font-display font-bold text-lg tabular-nums text-slate-900">{ds[k]}</span>
                                          ) : (
                                            <span className="text-slate-300">—</span>
                                          )}
                                        </td>
                                      ))}
                                      <td className="px-5 py-2.5 text-center">
                                        {j.dances?.[d]?.total != null ? (
                                          <span className="font-display font-bold text-lg tabular-nums text-blue-700 bg-blue-50 px-3 py-1 rounded-md">{j.dances[d].total}</span>
                                        ) : (
                                          <span className="text-slate-300">—</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="bg-emerald-50 border-t-2 border-emerald-200">
                                  <td className="px-5 py-2.5 font-display font-bold text-emerald-900 text-sm uppercase tracking-wider">Media</td>
                                  {["tecnica","coreografia","musicalita"].map((k) => (
                                    <td key={k} className="px-5 py-2.5 text-center">
                                      <span className="font-display font-bold text-base tabular-nums text-emerald-800">{fmtAvg(avgs[k])}</span>
                                    </td>
                                  ))}
                                  <td className="px-5 py-2.5 text-center">
                                    <span className="font-display font-bold text-base tabular-nums text-emerald-800">{fmtAvg(avgs.totale)}</span>
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            // SINGOLO branch
            return (
            <div key={a.id} className="rounded-2xl ring-1 ring-slate-200 overflow-hidden bg-white" data-testid={`result-athlete-${a.id}`}>
              <div className="bg-slate-50 px-5 py-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-slate-200">
                <span className="font-mono font-bold text-blue-700 text-2xl">{a.number}</span>
                <span className="font-display font-semibold text-slate-900 text-lg flex-1 min-w-0">{a.name}</span>
                <Badge variant="outline" className="border-slate-300 text-slate-700">{a.type}</Badge>
                <Button
                  size="sm"
                  onClick={() => handleDownloadAthlete(a.id, a.name)}
                  disabled={downloadingId === a.id}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`download-cert-${a.id}`}
                >
                  {downloadingId === a.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileBadge className="h-4 w-4 mr-1.5" />}
                  Attestato
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[600px]">
                  <thead className="bg-white text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <tr>
                      <th className="px-5 py-3 font-semibold">Giudice</th>
                      <th className="px-5 py-3 font-semibold w-28 text-center">Tecnica</th>
                      <th className="px-5 py-3 font-semibold w-28 text-center">Coreografia</th>
                      <th className="px-5 py-3 font-semibold w-28 text-center">Musicalità</th>
                      <th className="px-5 py-3 font-semibold w-28 text-center">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.judges.map((j) => (
                      <tr key={j.judge_id} className="border-t border-slate-100">
                        <td className="px-5 py-3">
                          <div className="font-medium text-slate-900 inline-flex items-center gap-2 flex-wrap">
                            <span>{j.judge_name}</span>
                            {j.finalized ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1 text-xs">
                                <CheckCircle2 className="h-3 w-3" /> Concluso
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 gap-1 text-xs">
                                <Clock className="h-3 w-3" /> In attesa
                              </Badge>
                            )}
                            <button
                              type="button"
                              onClick={() => openEdit(a, j)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline ml-1"
                              data-testid={`edit-scores-${a.id}-${j.judge_id}`}
                              title="Modifica voti di questo giudice"
                            >
                              <Pencil className="h-3 w-3" /> Modifica
                            </button>
                          </div>
                        </td>
                        {["tecnica", "coreografia", "musicalita"].map((k) => (
                          <td key={k} className="px-5 py-3 text-center">
                            {j.scores[k] != null ? (
                              <span className="font-display font-bold text-lg tabular-nums text-slate-900">{j.scores[k]}</span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        ))}
                        <td className="px-5 py-3 text-center">
                          {j.total != null ? (
                            <span className="font-display font-bold text-lg tabular-nums text-blue-700 bg-blue-50 px-3 py-1 rounded-md">{j.total}</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-emerald-50 border-t-2 border-emerald-200">
                      <td className="px-5 py-3 font-display font-bold text-emerald-900 text-sm uppercase tracking-wider">
                        Media
                      </td>
                      {["tecnica", "coreografia", "musicalita"].map((k) => (
                        <td key={k} className="px-5 py-3 text-center">
                          {a.averages?.[k] != null ? (
                            <span className="font-display font-bold text-base tabular-nums text-emerald-800">
                              {Number.isInteger(a.averages[k]) ? a.averages[k] : a.averages[k].toFixed(2).replace(/\.?0+$/, "")}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      ))}
                      <td className="px-5 py-3"></td>
                    </tr>
                    <tr className="bg-blue-50 border-t-2 border-blue-200">
                      <td className="px-5 py-3 font-display font-bold text-blue-900 text-sm uppercase tracking-wider" colSpan={4}>
                        Totale Generale
                      </td>
                      <td className="px-5 py-3 text-center">
                        {a.grand_total != null ? (
                          <span className="font-display font-bold text-xl tabular-nums text-white bg-blue-700 px-4 py-1.5 rounded-md inline-block min-w-[3rem]" data-testid={`grand-total-${a.id}`}>
                            {a.grand_total}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic text-sm">In attesa</span>
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-scores-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Modifica voti</DialogTitle>
            <DialogDescription>
              {editing && (
                <>
                  Atleta <span className="font-semibold text-slate-900">#{editing.athlete.number} {editing.athlete.name}</span> —
                  giudice <span className="font-semibold text-slate-900">{editing.judge.judge_name}</span>
                  {editing.dance && <> — ballo <span className="font-semibold text-fuchsia-700">{danceLabels[editing.dance] || editing.dance}</span></>}
                  . Inserisci valori interi da 1 a 10.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-2">
            {[
              { key: "tecnica", label: "Tecnica" },
              { key: "coreografia", label: "Coreografia" },
              { key: "musicalita", label: "Musicalità" },
            ].map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`edit-${f.key}`} className="text-xs uppercase tracking-wider font-semibold text-slate-600">{f.label}</Label>
                <Input
                  id={`edit-${f.key}`}
                  type="number"
                  min={1}
                  max={10}
                  step={1}
                  value={editScores[f.key]}
                  onChange={(e) => setEditScores((s) => ({ ...s, [f.key]: e.target.value }))}
                  className="text-center font-display text-lg tabular-nums"
                  data-testid={`edit-input-${f.key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving} data-testid="edit-cancel">Annulla</Button>
            <Button onClick={handleSaveScores} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="edit-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Salva e ricalcola
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
